import React from 'react';
import {
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    LinearProgress,
    Chip,
    Stack
} from '@mui/material';
import {
    Recycling,
    LocalFireDepartment,
    Timeline,
    TrendingUp,
    Schedule,
    CalendarToday
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

const UserStatsOverview = ({ userStats, user }) => {
    if (!userStats) return null;

    const statsCards = [
        {
            title: 'Total Classifications',
            value: userStats.totalClassifications,
            icon: <Recycling sx={{ fontSize: 40 }} />,
            color: '#43a047',
            gradient: 'linear-gradient(135deg, #43a047, #66bb6a)',
            description: 'Items sorted for environment'
        },
        {
            title: 'Current Streak',
            value: `${userStats.currentStreak} days`,
            icon: <LocalFireDepartment sx={{ fontSize: 40 }} />,
            color: '#ff5722',
            gradient: 'linear-gradient(135deg, #ff5722, #ff8a65)',
            description: 'Consecutive classification days'
        },
        {
            title: 'Accuracy Rate',
            value: `${userStats.accuracyRate?.toFixed(1) || 0}%`,
            icon: <TrendingUp sx={{ fontSize: 40 }} />,
            color: '#2196f3',
            gradient: 'linear-gradient(135deg, #2196f3, #64b5f6)',
            description: 'Average classification confidence'
        },
        {
            title: 'Weekly Points',
            value: userStats.weeklyPoints,
            icon: <Schedule sx={{ fontSize: 40 }} />,
            color: '#ff9800',
            gradient: 'linear-gradient(135deg, #ff9800, #ffb74d)',
            description: 'Points earned this week'
        },
        {
            title: 'Monthly Points',
            value: userStats.monthlyPoints,
            icon: <CalendarToday sx={{ fontSize: 40 }} />,
            color: '#9c27b0',
            gradient: 'linear-gradient(135deg, #9c27b0, #ba68c8)',
            description: 'Points earned this month'
        },
        {
            title: 'Total Impact',
            value: userStats.totalPoints,
            icon: <Timeline sx={{ fontSize: 40 }} />,
            color: '#4caf50',
            gradient: 'linear-gradient(135deg, #4caf50, #81c784)',
            description: 'All-time eco points earned'
        }
    ];

    return (
        <Box sx={{ mb: 4 }}>
            <Typography variant="h4" fontWeight={800} sx={{ mb: 3, color: '#1b5e20', textAlign: 'center' }}>
                📊 Your Environmental Impact Statistics
            </Typography>

            <Grid container spacing={3}>
                {statsCards.map((stat, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                        <Card
                            elevation={6}
                            sx={{
                                height: '100%',
                                borderRadius: 4,
                                background: `linear-gradient(135deg, ${alpha(stat.color, 0.1)}, rgba(255,255,255,0.95))`,
                                border: `2px solid ${alpha(stat.color, 0.2)}`,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    transform: 'translateY(-8px) scale(1.02)',
                                    boxShadow: `0 20px 40px ${alpha(stat.color, 0.25)}`,
                                    border: `2px solid ${stat.color}`
                                }
                            }}
                        >
                            <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                <Box sx={{
                                    width: 80,
                                    height: 80,
                                    borderRadius: '50%',
                                    background: stat.gradient,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 20px',
                                    boxShadow: `0 8px 32px ${alpha(stat.color, 0.3)}`,
                                    color: 'white'
                                }}>
                                    {stat.icon}
                                </Box>

                                <Typography variant="h3" fontWeight={900} sx={{
                                    color: stat.color,
                                    mb: 1,
                                    textShadow: `0 2px 8px ${alpha(stat.color, 0.3)}`
                                }}>
                                    {stat.value}
                                </Typography>

                                <Typography variant="h6" fontWeight={700} sx={{
                                    color: '#1b5e20',
                                    mb: 1
                                }}>
                                    {stat.title}
                                </Typography>

                                <Typography variant="body2" color="text.secondary" sx={{
                                    fontStyle: 'italic',
                                    fontSize: '0.9rem'
                                }}>
                                    {stat.description}
                                </Typography>

                                {/* Progress indicator for certain stats */}
                                {(stat.title.includes('Streak') && userStats.currentStreak > 0) && (
                                    <Box sx={{ mt: 2 }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={Math.min(100, (userStats.currentStreak / 30) * 100)}
                                            sx={{
                                                height: 8,
                                                borderRadius: 4,
                                                backgroundColor: alpha(stat.color, 0.2),
                                                '& .MuiLinearProgress-bar': {
                                                    background: stat.gradient,
                                                    borderRadius: 4
                                                }
                                            }}
                                        />
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                            {Math.min(100, Math.round((userStats.currentStreak / 30) * 100))}% to monthly streak goal
                                        </Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Quick Achievement Summary */}
            <Card elevation={4} sx={{
                mt: 3,
                borderRadius: 4,
                background: 'linear-gradient(135deg, rgba(67, 160, 71, 0.1), rgba(102, 187, 106, 0.05))',
                border: '2px solid rgba(67, 160, 71, 0.2)'
            }}>
                <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: '#1b5e20', textAlign: 'center' }}>
                        🎯 Quick Achievement Overview
                    </Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap" justifyContent="center">
                        <Chip
                            label={`${userStats.totalBadges} Badges Unlocked`}
                            sx={{
                                background: 'linear-gradient(135deg, #ffc400, #ff8f00)',
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.9rem'
                            }}
                        />
                        <Chip
                            label={`${userStats.currentLevel?.name || 'Rookie'} Level`}
                            sx={{
                                background: `linear-gradient(135deg, ${userStats.currentLevel?.color || '#66bb6a'}, ${alpha(userStats.currentLevel?.color || '#66bb6a', 0.8)})`,
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.9rem'
                            }}
                        />
                        {userStats.globalRank > 0 && (
                            <Chip
                                label={`Rank #${userStats.globalRank}`}
                                sx={{
                                    background: 'linear-gradient(135deg, #e91e63, #f06292)',
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: '0.9rem'
                                }}
                            />
                        )}
                        <Chip
                            label={`${((userStats.lastActivity ? (Date.now() - new Date(userStats.lastActivity).getTime()) / (1000 * 60 * 60 * 24) : 0) < 1) ? 'Active Today' : `${Math.floor((Date.now() - new Date(userStats.lastActivity || new Date()).getTime()) / (1000 * 60 * 60 * 24))} days ago`}`}
                            sx={{
                                background: userStats.lastActivity && (Date.now() - new Date(userStats.lastActivity).getTime()) / (1000 * 60 * 60 * 24) < 1
                                    ? 'linear-gradient(135deg, #4caf50, #66bb6a)'
                                    : 'linear-gradient(135deg, #757575, #9e9e9e)',
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.9rem'
                            }}
                        />
                    </Stack>
                </CardContent>
            </Card>
        </Box>
    );
};

export default UserStatsOverview;
